// features/intelligence/domain-briefing.js
// ═══════════════════════════════════════════════════════════
// DOMAIN BRIEFING — full-screen 6-tab dashboard for the
// selected domain.
//
// Layout: single-column flex inside the existing app shell.
//   Topbar      — [< Trading] | [Dashboard] [Briefing] ...
//   Market bar  — live SPY · QQQ · IWM · VIX · clock
//   Content     — active tab body
//
// On mount we collapse the AppSidebar so the content has the
// full width AND fetch the live bundle from
// /api/intelligence/trading. The bundle has the same shape as
// the sample-trading-domain bundle but with live values
// overlaid section-by-section + freshness metadata so the UI
// can show "as of HH:MM" timestamps.
//
// On fetch error we keep the sample bundle — the dashboard is
// never blank, just (silently) showing sample data.
//
// Polling: refresh every 60s while the tab is visible. The API
// route is CDN-cached for 60s anyway so we're not hammering
// the DB.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import DashboardTab from './tabs/dashboard';
import BriefingTab from './tabs/briefing';
import DomainKnowledgeTab from './tabs/domain-knowledge';
import SourcesTab from './tabs/sources';
import SignalMatrixTab from './tabs/signal-matrix';
import CommunityTab from './tabs/community';
import { TRADING_DOMAIN } from '../../lib/intelligence/sample-trading-domain';

const SAMPLE_BUNDLES = {
  trading: TRADING_DOMAIN,
};

const API_ENDPOINT = {
  trading: '/api/intelligence/trading',
};

const TABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'briefing',  label: 'Briefing' },
  { key: 'knowledge', label: 'Knowledge' },
  { key: 'sources',   label: 'Sources' },
  { key: 'signal',    label: 'Signal' },
  { key: 'community', label: 'Community' },
];

const REFRESH_MS = 60_000; // every minute

export default function DomainBriefing({
  domainKey = 'trading',
  onExitDomain,
  onSidebarCollapse,
}) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [bundle, setBundle] = useState(SAMPLE_BUNDLES[domainKey] || TRADING_DOMAIN);

  // Auto-collapse the AppSidebar on mount
  useEffect(() => {
    onSidebarCollapse?.(true);
  }, [onSidebarCollapse]);

  // Fetch live bundle on mount + refresh every 60s while visible
  useEffect(() => {
    const endpoint = API_ENDPOINT[domainKey];
    if (!endpoint) return;
    let cancelled = false;

    async function refresh() {
      try {
        const res = await fetch(endpoint, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data?.bundle) return;
        setBundle(data.bundle);
      } catch (err) {
        // Silent — sample bundle stays in place. Logs the
        // error so it's visible in browser dev tools.
        console.warn('[domain-briefing] live fetch failed:', err.message);
      }
    }

    refresh();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, REFRESH_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [domainKey]);

  function handleLensPathway(pathway, _lens) {
    if (typeof console !== 'undefined') {
      console.log('[di] pathway clicked:', pathway);
    }
  }

  return (
    <div className="di-root">
      <Topbar
        domainName={bundle.meta.domainName}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onBack={onExitDomain}
      />
      <MarketBar bundle={bundle} />
      {activeTab === 'dashboard'  && <DashboardTab bundle={bundle} onLensPathway={handleLensPathway} />}
      {activeTab === 'briefing'   && <BriefingTab />}
      {activeTab === 'knowledge'  && <DomainKnowledgeTab />}
      {activeTab === 'sources'    && <SourcesTab />}
      {activeTab === 'signal'     && <SignalMatrixTab />}
      {activeTab === 'community'  && <CommunityTab />}
    </div>
  );
}

// ── Topbar — back arrow, domain label, horizontal tabs ─────
function Topbar({ domainName, activeTab, onTabChange, onBack }) {
  return (
    <div className="di-topbar">
      <div className="di-topbar-domain">
        <button
          type="button"
          className="di-topbar-back"
          onClick={onBack}
          aria-label="Back to Domain Selector"
          title="Back to all domains"
        >
          ‹
        </button>
        <div className="di-topbar-domain-dot" />
        <div className="di-topbar-domain-name">{domainName}</div>
      </div>
      <div className="di-topbar-tabs" role="tablist">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === activeTab}
            className={`di-topbar-tab${t.key === activeTab ? ' active' : ''}`}
            onClick={() => onTabChange?.(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Market sub-bar — live data, secondary to navigation ────
function MarketBar({ bundle }) {
  const chips = bundle.topbar;
  const fresh = bundle._freshness?.topbar;
  return (
    <div className="di-market-bar">
      {chips.map(c => (
        <span key={c.label} className="di-mkt-chip">
          <span className="di-mkt-l">{c.label}</span>
          <span className="di-mkt-v">{c.value}</span>
          <span className={`di-mkt-c ${c.dir}`}>{c.change}</span>
        </span>
      ))}
      <div className="di-mkt-time">
        {fresh?.source === 'live' && fresh.fetchedAt
          ? `Live · ${formatTime(fresh.fetchedAt)}`
          : bundle.meta.asOf}
      </div>
    </div>
  );
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    });
  } catch (_e) {
    return '';
  }
}
